import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { storePayoutsApi } from '../services/api';
import { ACTION_COPY, describePayoutActionError, releasesEarly } from './storePayoutsUtils';

/**
 * Confirmation for one payout action (release / hold / resume / retry). None of them take a body or
 * a reason. On an unknown-outcome error the dialog stays open so the admin can repeat the identical
 * call (the server reuses the same idempotency key, so it cannot pay twice).
 */
const PayoutActionDialog = ({ payout, action, onClose, onDone }) => {
  const copy = ACTION_COPY[action];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await storePayoutsApi[action](payout.orderId);
      onDone(result, action);
    } catch (e) {
      setError(describePayoutActionError(e, action));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    if (error?.refresh) onDone(null, action);
    else onClose();
  };

  // Retry uses the same admin release path, so it is flagged the same way.
  const early = (action === 'release' || action === 'retry') && releasesEarly(payout);

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{copy.title} (order #{payout.orderId})</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>{copy.body(payout)}</Typography>
        {early && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This release may be flagged as released before the return window closed (&quot;Paid before the
            window closed&quot;): it is ahead of the normal payout date (return window + buffer), or a return,
            a dispute or a hold may still be open.
          </Alert>
        )}
        {action === 'release' && !early && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This payout is past its normal date, so it is normally not flagged. It still will be if an admin
            lengthened the return window since delivery, or a store rejection can still be disputed by the buyer.
          </Alert>
        )}
        {error && (
          <Alert severity={error.retrySafe ? 'warning' : 'error'} sx={{ mb: 2 }}>{error.message}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>{error?.stale ? 'Close' : 'Cancel'}</Button>
        {!error?.stale && (
          <Button
            variant="contained"
            color={action === 'hold' ? 'warning' : 'primary'}
            onClick={submit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {error?.retrySafe ? 'Confirm again' : copy.confirm}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PayoutActionDialog;
